/* Mobile is deliberately one gesture / one adjacent step, without momentum. */
(() => {
  const interactive = 'a,button,input,textarea,select,summary,[contenteditable="true"]';
  const axisFor = (dx, dy) => Math.max(Math.abs(dx), Math.abs(dy)) < 8 ? null : Math.abs(dy) >= Math.abs(dx) * 0.9 ? 'vertical' : 'horizontal';
  const adjacentIndex = ({ index, count, distance, width, duration }) => {
    const threshold = Math.min(64, Math.max(24, width * 0.14));
    const flick = Math.abs(distance) >= 18 && duration > 0 && duration < 220 && Math.abs(distance) / duration > 0.35;
    const direction = Math.abs(distance) >= threshold || flick ? Math.sign(distance) : 0;
    return Math.max(0, Math.min(count - 1, index + direction));
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = { axisFor, adjacentIndex };
  if (typeof customElements === 'undefined' || customElements.get('aven-contour-ritual-carousel')) return;

  class AvenContourRitualCarousel extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.track = this.querySelector('.aven-contour-ritual__steps');
      this.steps = [...this.querySelectorAll('.aven-contour-ritual__step')];
      this.status = this.querySelector('[data-ritual-status]');
      this.previous = this.querySelector('[data-ritual-prev]');
      this.next = this.querySelector('[data-ritual-next]');
      this.counter = this.querySelector('[data-ritual-count]');
      if (!this.track || this.steps.length < 2 || !window.PointerEvent) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.mobile = matchMedia('(max-width: 749px)');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.index = 0;
      this.originalStates = this.steps.map(step => ({ hidden: step.getAttribute('aria-hidden'), inert: step.inert }));
      this.track.addEventListener('pointerdown', event => this.start(event), options);
      this.track.addEventListener('pointermove', event => this.move(event), { ...options, passive: false });
      this.track.addEventListener('pointerup', event => this.finish(event), options);
      this.track.addEventListener('pointercancel', event => this.cancel(event), options);
      this.track.addEventListener('lostpointercapture', event => {
        if (event.target === this.track) this.cancel(event);
      }, options);
      this.previous?.addEventListener('click', () => this.navigate(-1), options);
      this.next?.addEventListener('click', () => this.navigate(1), options);
      this.track.addEventListener('wheel', event => this.wheel(event), { ...options, passive: false });
      // Mouse/touch emulation may leave the track before directional intent
      // is known. End that gesture even when its release occurs elsewhere.
      window.addEventListener('pointerup', event => this.finish(event), options);
      window.addEventListener('pointercancel', event => this.cancel(event), options);
      window.addEventListener('blur', () => this.cancel(), options);
      this.track.addEventListener('keydown', event => this.key(event), options);
      this.track.addEventListener('click', event => {
        if (performance.now() < (this.suppressClickUntil || 0)) { event.preventDefault(); event.stopPropagation(); }
      }, { ...options, capture: true });
      this.mobile.addEventListener('change', () => this.configure(), options);
      this.addEventListener('shopify:block:select', event => {
        if (!this.mobile.matches) return;
        const index = this.steps.findIndex(step => step === event.target || step.contains(event.target));
        if (index >= 0) this.goTo(index, false);
      }, options);
      this.resize = new ResizeObserver(() => { if (this.mobile.matches) { this.cancel(); this.goTo(this.index, false); } });
      this.resize.observe(this.track);
      this.configure();
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.resize?.disconnect();
      this.releasePointer();
      this.restoreSteps();
      this.removeAttribute('data-ritual-enhanced');
      this.abort = null;
    }
    configure() {
      this.releasePointer();
      this.toggleAttribute('data-ritual-enhanced', this.mobile.matches);
      if (this.mobile.matches) this.goTo(this.index, false);
      else {
        this.restoreSteps();
        if (this.status) this.status.textContent = '';
        this.track.scrollTo({ left: 0, behavior: 'auto' });
      }
    }
    position(index) { return this.steps[index].offsetLeft - this.steps[0].offsetLeft; }
    goTo(index, smooth = true) {
      this.index = Math.max(0, Math.min(this.steps.length - 1, index));
      this.track.scrollTo({ left: this.position(this.index), behavior: smooth && !this.reduced.matches ? 'smooth' : 'auto' });
      this.steps.forEach((step, number) => {
        step.inert = number !== this.index;
        if (number !== this.index) step.setAttribute('aria-hidden', 'true');
        else step.removeAttribute('aria-hidden');
      });
      if (this.status) this.status.textContent = (this.getAttribute('data-step-status') || 'Step [current] of [total]').replace('[current]', this.index + 1).replace('[total]', this.steps.length);
      if (this.counter) this.counter.textContent = `${this.index + 1} / ${this.steps.length}`;
      if (this.previous) this.previous.disabled = this.index === 0;
      if (this.next) this.next.disabled = this.index === this.steps.length - 1;
    }
    restoreSteps() {
      this.steps?.forEach((step, index) => {
        const original = this.originalStates?.[index];
        if (!original) return;
        step.inert = original.inert;
        if (original.hidden === null) step.removeAttribute('aria-hidden');
        else step.setAttribute('aria-hidden', original.hidden);
      });
    }
    start(event) {
      if (!this.mobile.matches || event.isPrimary === false || event.button !== 0 || this.gesture || event.target.closest(interactive)) return;
      // Interrupt any previous animation at its committed step before a new gesture.
      this.track.scrollTo({ left: this.position(this.index), behavior: 'auto' });
      this.gesture = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, started: event.timeStamp, index: this.index, axis: null, distance: 0 };
    }
    move(event) {
      const gesture = this.gesture;
      if (!gesture || event.pointerId !== gesture.pointerId) return;
      const dx = event.clientX - gesture.x;
      const dy = event.clientY - gesture.y;
      gesture.axis ||= axisFor(dx, dy);
      if (gesture.axis !== 'horizontal') return;
      if (event.cancelable) event.preventDefault();
      if (!this.track.hasPointerCapture(event.pointerId)) this.track.setPointerCapture(event.pointerId);
      gesture.distance = -dx;
      const start = this.position(gesture.index);
      const before = this.position(Math.max(0, gesture.index - 1));
      const after = this.position(Math.min(this.steps.length - 1, gesture.index + 1));
      // A long or forceful swipe cannot expose a second-next step.
      this.track.scrollLeft = Math.max(before, Math.min(after, start + gesture.distance));
    }
    finish(event) {
      const gesture = this.gesture;
      if (!gesture || event.pointerId !== gesture.pointerId) return;
      const horizontal = gesture.axis === 'horizontal';
      const next = horizontal ? adjacentIndex({ index: gesture.index, count: this.steps.length, distance: gesture.distance, width: this.track.clientWidth, duration: event.timeStamp - gesture.started }) : gesture.index;
      if (horizontal) this.suppressClickUntil = performance.now() + 400;
      this.releasePointer();
      if (horizontal) this.goTo(next);
    }
    cancel(event) {
      if (!this.gesture || event && event.pointerId !== this.gesture.pointerId) return;
      const index = this.gesture.index;
      const horizontal = this.gesture.axis === 'horizontal';
      this.releasePointer();
      if (horizontal && this.mobile.matches) this.goTo(index, false);
    }
    releasePointer() {
      const pointerId = this.gesture?.pointerId;
      this.gesture = null;
      if (pointerId !== undefined && this.track?.hasPointerCapture(pointerId)) this.track.releasePointerCapture(pointerId);
    }
    navigate(direction) {
      if (!this.mobile.matches) return;
      this.cancel();
      this.goTo(this.index + direction);
    }
    wheel(event) {
      if (!this.mobile.matches || Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
      if (event.cancelable) event.preventDefault();
      const now = performance.now();
      const newGesture = !this.lastWheelTime || now - this.lastWheelTime > 200;
      this.lastWheelTime = now;
      if (newGesture && Math.abs(event.deltaX) >= 8) this.navigate(Math.sign(event.deltaX));
    }
    key(event) {
      if (!this.mobile.matches || event.target.closest(interactive)) return;
      const direction = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
      if (!direction) return;
      event.preventDefault();
      this.cancel();
      this.goTo(this.index + direction);
    }
  }
  customElements.define('aven-contour-ritual-carousel', AvenContourRitualCarousel);
})();
